import "bootstrap";
import { Widget } from "@foretagsplatsen/widgetjs";

/**
 * Renders a button for an action and a caret button to display
 * secondary actions in a dropdown menu.
 *
 * @param {object[]} elements - Action specifications. Each element is either
 * an object with keys among {label, klass, icon, action, href, dataTestName},
 * or a renderable. In the latter case, the renderable will be appended as is.
 * @param {boolean} [alignRight] - If true, align the dropdown menu to
 * the right of the widget. Otherwise, align it to the left.
 * @param {string} [dataTestName] - Used to select the receiver in UI
 * tests.
 */
export default class ButtonDropdown extends Widget {
	constructor({ elements, alignRight = false, dataTestName }) {
		super(...arguments);

		this._elements = elements;
		this._alignRight = alignRight;
		this._dataTestName = dataTestName;
	}

	renderContentOn(html) {
		html.div(
			{ class: "btn-group", "data-test": this._dataTestName },
			this._renderMainButtonOn.bind(this),
			this._renderDropdownOn.bind(this),
		);
	}

	_renderMainButtonOn(html) {
		html.render(
			this._elementRenderer({
				klass: "btn btn-default",
				...this._elements[0],
			}),
		);
	}

	_renderDropdownOn(html) {
		if (!this._hasElementsForDropdown()) {
			return;
		}

		this._renderDropdownButtonOn(html);
		this._renderDropdownElementsOn(html);
	}

	_renderDropdownButtonOn(html) {
		html.button(
			{
				klass: "btn btn-default dropdown-toggle",
				"data-toggle": "dropdown",
				"aria-haspopup": true,
				"aria-expanded": false,
				"data-test": "extra-actions",
			},
			html.span({ klass: "caret", "data-test": "toggle-dropdown" }),
			html.span({ klass: "sr-only" }, "Toggle Dropdown"),
		);
	}

	_renderDropdownElementsOn(html) {
		html.ul(
			{
				"data-test": "dropdown-menu",
				klass: ["dropdown-menu", { "pull-right": this._alignRight }],
			},
			this._elementsForDropdown().map(
				(element) => (html) => html.li(this._elementRenderer(element)),
			),
		);
	}

	_elementRenderer(element) {
		if (element.appendTo) {
			return element;
		}

		const {
			label,
			klass = "",
			icon,
			action,
			href,
			dataTestName,
			download = false,
		} = element;

		const options = {
			class: [klass, { "icon-and-text": icon }],
			"data-test": dataTestName,
		};

		if (download) {
			options.download = download;
		}

		return (html) => {
			const anchor = html.a(
				options,
				(html) => icon && html.i({ class: `${icon}` }),
				label,
			);

			action && anchor.click(action);
			href && anchor.href(href);
		};
	}

	_elementsForDropdown() {
		return this._elements.slice(1);
	}

	_hasElementsForDropdown() {
		return this._elements.length > 1;
	}
}
